// conferences.js
// Firebase conferences and conference_registrations collection API functions

import { 
  collection, 
  doc, 
  getDoc, 
  getDocs, 
  addDoc, 
  updateDoc, 
  deleteDoc,
  serverTimestamp,
  query,
  where,
  orderBy,
  runTransaction
} from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { db, storage } from '../firebaseConfig';

const CONFERENCES_COLLECTION = 'conferences';
const REGISTRATIONS_COLLECTION = 'conference_registrations';

/**
 * Get all conferences
 */
export const getAllConferences = async () => {
  try {
    const conferencesRef = collection(db, CONFERENCES_COLLECTION);
    const q = query(conferencesRef, orderBy('createdAt', 'desc'));
    const querySnapshot = await getDocs(q);
    
    const conferences = [];
    querySnapshot.forEach((doc) => {
      conferences.push({
        id: doc.id,
        ...doc.data(),
      });
    });
    
    return conferences;
  } catch (error) {
    console.error('Error getting conferences:', error);
    throw error;
  }
};

/**
 * Get conference by ID
 */
export const getConferenceById = async (conferenceId) => {
  try {
    const conferenceRef = doc(db, CONFERENCES_COLLECTION, conferenceId);
    const conferenceSnap = await getDoc(conferenceRef);
    
    if (conferenceSnap.exists()) {
      return {
        id: conferenceSnap.id,
        ...conferenceSnap.data(),
      };
    } else {
      throw new Error('Conference not found');
    }
  } catch (error) {
    console.error('Error getting conference:', error);
    throw error;
  }
};

/**
 * Create new conference (admin only)
 */
export const createConference = async (conferenceData) => {
  try {
    const conferencesRef = collection(db, CONFERENCES_COLLECTION);
    const newConference = {
      ...conferenceData,
      sponsoredSlots: parseInt(conferenceData.sponsoredSlots) || 0,
      primaryRegisteredCount: 0,
      active: conferenceData.active !== undefined ? conferenceData.active : true,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    };
    
    const docRef = await addDoc(conferencesRef, newConference);
    return {
      id: docRef.id,
      ...newConference,
    };
  } catch (error) {
    console.error('Error creating conference:', error);
    throw error;
  }
};

/**
 * Update conference (admin only)
 */
export const updateConference = async (conferenceId, conferenceData) => {
  try {
    const conferenceRef = doc(db, CONFERENCES_COLLECTION, conferenceId);
    const updateData = {
      ...conferenceData,
      sponsoredSlots: parseInt(conferenceData.sponsoredSlots) || 0,
      updatedAt: serverTimestamp(),
    };
    
    await updateDoc(conferenceRef, updateData);
    
    // Also recalculate counts just in case slots were updated
    await recalculateConferenceCounts(conferenceId);
    
    return { id: conferenceId, ...updateData };
  } catch (error) {
    console.error('Error updating conference:', error);
    throw error;
  }
};

/**
 * Delete conference (admin only)
 */
export const deleteConference = async (conferenceId) => {
  try {
    // Delete all registrations for this conference first, along with their PDF files
    const registrations = await getConferenceRegistrations(conferenceId);
    for (const reg of registrations) {
      await deleteRegistration(reg.id, conferenceId);
    }

    const conferenceRef = doc(db, CONFERENCES_COLLECTION, conferenceId);
    await deleteDoc(conferenceRef);
  } catch (error) {
    console.error('Error deleting conference:', error);
    throw error;
  }
};

/**
 * Upload abstract PDF to Firebase Storage
 */
export const uploadAbstractPDF = async (file, userId) => {
  try {
    if (!file) {
      throw new Error('No PDF file provided');
    }

    if (file.type !== 'application/pdf') {
      throw new Error('Invalid file type. Please upload a valid PDF document.');
    }

    // Validate size (10MB limit)
    const maxSize = 10 * 1024 * 1024;
    if (file.size > maxSize) {
      throw new Error('File size exceeds 10MB limit');
    }

    const timestamp = Date.now();
    const cleanFileName = file.name.replace(/[^a-zA-Z0-9.]/g, '_');
    const storagePath = `conference_applications/${userId}/${timestamp}_${cleanFileName}`;
    const storageRef = ref(storage, storagePath);

    const metadata = {
      contentType: file.type,
      customMetadata: {
        uploadedBy: userId,
        uploadedAt: new Date().toISOString()
      }
    };

    const snapshot = await uploadBytes(storageRef, file, metadata);
    const downloadURL = await getDownloadURL(snapshot.ref);

    return {
      pdfUrl: downloadURL,
      pdfName: file.name
    };
  } catch (error) {
    console.error('Error uploading abstract PDF:', error);
    throw error;
  }
};

/**
 * Register user for a conference with Transactional safety
 */
export const registerForConference = async (userId, regData, pdfFile) => {
  try {
    const { conferenceId } = regData;
    if (!conferenceId) {
      throw new Error('Conference selection is required.');
    }

    // 1. Upload the PDF file
    const { pdfUrl, pdfName } = await uploadAbstractPDF(pdfFile, userId);

    // 2. Prepare references
    const conferenceRef = doc(db, CONFERENCES_COLLECTION, conferenceId);
    const newRegRef = doc(collection(db, REGISTRATIONS_COLLECTION));

    // 3. Execute transactional write to check slot limit and set status
    let finalStatus = 'reserve';
    
    const result = await runTransaction(db, async (transaction) => {
      const confSnap = await transaction.get(conferenceRef);
      if (!confSnap.exists()) {
        throw new Error('Conference not found.');
      }

      const confData = confSnap.data();
      const sponsoredSlots = parseInt(confData.sponsoredSlots) || 0;
      const currentPrimaryCount = parseInt(confData.primaryRegisteredCount) || 0;

      let nextPrimaryCount = currentPrimaryCount;

      if (currentPrimaryCount < sponsoredSlots) {
        finalStatus = 'primary';
        nextPrimaryCount = currentPrimaryCount + 1;
      } else {
        finalStatus = 'reserve';
      }

      const registrationData = {
        userId,
        fullNameArabic: regData.fullNameArabic,
        fullNameEnglish: regData.fullNameEnglish,
        jobTitle: regData.jobTitle,
        workplace: regData.workplace,
        phone: regData.phone,
        email: regData.email,
        isMember: regData.isMember,
        previouslyRepresented: regData.previouslyRepresented,
        conferenceId,
        conferenceName: confData.name,
        researchTitle: regData.researchTitle,
        pdfUrl,
        pdfName,
        status: finalStatus,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      };

      // Set the registration document
      transaction.set(newRegRef, registrationData);

      // Update the conference count
      transaction.update(conferenceRef, {
        primaryRegisteredCount: nextPrimaryCount,
        updatedAt: serverTimestamp()
      });

      return {
        id: newRegRef.id,
        ...registrationData
      };
    });

    return result;
  } catch (error) {
    console.error('Error registering for conference:', error);
    throw error;
  }
};

/**
 * Get all registrations for a conference (admin only)
 */
export const getConferenceRegistrations = async (conferenceId) => {
  try {
    const regsRef = collection(db, REGISTRATIONS_COLLECTION);
    const q = query(
      regsRef, 
      where('conferenceId', '==', conferenceId)
    );
    const querySnapshot = await getDocs(q);
    
    const registrations = [];
    querySnapshot.forEach((doc) => {
      registrations.push({
        id: doc.id,
        ...doc.data(),
      });
    });
    
    // Sort client-side by createdAt ascending to avoid index requirements
    registrations.sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt));
    
    return registrations;
  } catch (error) {
    console.error('Error getting conference registrations:', error);
    throw error;
  }
};

/**
 * Get registrations for a specific user
 */
export const getUserConferenceRegistrations = async (userId) => {
  try {
    const regsRef = collection(db, REGISTRATIONS_COLLECTION);
    const q = query(
      regsRef,
      where('userId', '==', userId)
    );
    const querySnapshot = await getDocs(q);
    
    const registrations = [];
    querySnapshot.forEach((doc) => {
      registrations.push({
        id: doc.id,
        ...doc.data(),
      });
    });
    
    // Sort client-side by createdAt descending to avoid index requirements
    registrations.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
    
    return registrations;
  } catch (error) {
    console.error('Error getting user registrations:', error);
    throw error;
  }
};

/**
 * Update registration status (admin only)
 */
export const updateRegistrationStatus = async (registrationId, status, conferenceId) => {
  try {
    const regRef = doc(db, REGISTRATIONS_COLLECTION, registrationId);
    await updateDoc(regRef, {
      status,
      updatedAt: serverTimestamp(),
    });

    // Recalculate counts for this conference
    if (conferenceId) {
      await recalculateConferenceCounts(conferenceId);
    }
  } catch (error) {
    console.error('Error updating registration status:', error);
    throw error;
  }
};

/**
 * Delete registration (admin only)
 */
export const deleteRegistration = async (registrationId, conferenceId) => {
  try {
    const regRef = doc(db, REGISTRATIONS_COLLECTION, registrationId);
    const regSnap = await getDoc(regRef);

    if (regSnap.exists()) {
      const regData = regSnap.data();
      const pdfUrl = regData.pdfUrl;

      // Delete PDF from storage
      if (pdfUrl) {
        try {
          const decodedUrl = decodeURIComponent(pdfUrl);
          const pathMatch = decodedUrl.match(/\/o\/(.+?)\?/);
          if (pathMatch && pathMatch[1]) {
            const pdfRef = ref(storage, pathMatch[1]);
            await deleteObject(pdfRef);
          }
        } catch (storageErr) {
          console.warn('Could not delete PDF file from storage:', storageErr);
        }
      }
    }

    // Delete Firestore document
    await deleteDoc(regRef);

    // Recalculate counts
    if (conferenceId) {
      await recalculateConferenceCounts(conferenceId);
    }
  } catch (error) {
    console.error('Error deleting registration:', error);
    throw error;
  }
};

/**
 * Recalculate primary application counts for a conference and update its counter
 */
export const recalculateConferenceCounts = async (conferenceId) => {
  try {
    const regsRef = collection(db, REGISTRATIONS_COLLECTION);
    const q = query(
      regsRef, 
      where('conferenceId', '==', conferenceId), 
      where('status', '==', 'primary')
    );
    const snap = await getDocs(q);
    const count = snap.size;

    const conferenceRef = doc(db, CONFERENCES_COLLECTION, conferenceId);
    await updateDoc(conferenceRef, {
      primaryRegisteredCount: count,
      updatedAt: serverTimestamp()
    });
  } catch (error) {
    console.error('Error recalculating conference counts:', error);
  }
};

/**
 * Seed default conferences if collection is empty
 */
export const seedDefaultConferences = async () => {
  try {
    const conferencesRef = collection(db, CONFERENCES_COLLECTION);
    const snapshot = await getDocs(conferencesRef);

    if (snapshot.empty) {
      console.log('Seeding default conferences...');
      
      const defaultConferences = [
        {
          name: 'Second Qatar Uveitis Conference',
          date: '2026-09-25', // Use standard dates for sorting/filtering but can keep text display
          dateLabel: '25 to 26 September 2026',
          location: 'Qatar',
          sponsoredSlots: 2,
          primaryRegisteredCount: 0,
          active: true,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString()
        },
        {
          name: 'SOS Conference 2026',
          date: '2026-11-05',
          dateLabel: '5 to 7 November 2026',
          location: 'Al Khobar, Saudi Arabia',
          sponsoredSlots: 3,
          primaryRegisteredCount: 0,
          active: true,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString()
        }
      ];

      for (const conf of defaultConferences) {
        await addDoc(conferencesRef, conf);
      }
      
      return true;
    }
    
    return false;
  } catch (error) {
    console.error('Error seeding default conferences:', error);
    throw error;
  }
};
